
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { generateReportQuery } from '@/utils/reportQueryGenerator';
import ReportBuilderPreview from '@/components/ReportBuilderPreview.vue';
import OnboardingBanner from '@/components/OnboardingBanner.vue';
import { CustomReportModel } from '@/models/custom_report';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { ReportBuilderEventHandler } from '@/modules/reportBuilderEvents';
// @ts-ignore
import { EventBus } from '@/main';

@Component({
  name: 'ReportBuilder',
  components: {
    ReportBuilderPreview,
    OnboardingBanner
  }
})
export default class ReportBuilder extends Vue {
  query: string = '';
  loading: boolean = false;
  error: string | null = null;
  reportUrl: string | null = null;
  reportName: string | null = null;
  confidenceScore: number | null = null;
  inputFocused: boolean = false;
  hoveredChip: number | null = null;
  processingStage: 'understanding' | 'analyzing' | 'generating' = 'understanding';
  stageInterval: any = null;
  dataReadyTimeout: any = null; // Timeout for hiding data ready message
  aiResponse: any = null; // Store the full AI response for preview
  successSectionCollapsed: boolean = true; // Success section collapsed by default
  loadingSaveReport: boolean = false;
  savedReportUrl: string | null = null;
  savedReportMessage: string = 'Report saved successfully!';
  savedReportLinkText: string = 'View saved report';
  @Prop({ default: false })
  public isAthenaReady!: boolean;

  // Event tracking properties
  private eventHandler!: ReportBuilderEventHandler;

  // Track chatbox state to hide input section when chatbox is open
  isChatboxOpen: boolean = false;

  // Onboarding mode state - when true, shows sample data while Shopify import is in progress
  onboardingMode: boolean = false;
  isDataImportComplete: boolean = false;
  showDataReadyMessage: boolean = false;

  // EventBus handler references for proper cleanup
  private onAgentSelectedHandler: ((data: any) => void) | null = null;
  private onConversationQualityHandler: ((data: any) => void) | null = null;

  /**
   * Watch for isAthenaReady to automatically disable onboarding mode
   * when data import is complete
   */
  @Watch('isAthenaReady')
  onAthenaReady(newValue: boolean, oldValue: boolean) {
    if (newValue && !oldValue && this.onboardingMode) {
      // Data became ready - disable onboarding mode
      console.log('[ReportBuilder] Data import completed, disabling onboarding mode');
      this.onboardingMode = false;

      // Safely remove from localStorage
      try {
        localStorage.removeItem('assisty_onboarding_mode');
      } catch (error) {
        console.warn('[ReportBuilder] Failed to remove onboarding mode from localStorage:', error);
      }

      this.isDataImportComplete = true;
      this.showDataReadyMessage = true;

      // Hide the data ready message after 5 seconds
      this.dataReadyTimeout = setTimeout(() => {
        this.showDataReadyMessage = false;
      }, 5000);
    }
  }

  defaultQueries: string[] = [
    'Which products are performing best this month?',
    'What products should I reorder now? Give me a list of products that are low in stock and have a high sales velocity.',
    'How much revenue did I lose from out-of-stock products last month?',
    'Show me current inventory levels of the top 10 products by revenue',
    'Forecast inventory for the products with high sales velocity, show me the products with the highest sales velocity and the forecasted inventory levels'
  ];
  
  exampleQueries: string[] = [...this.defaultQueries];
  hasAISuggestions: boolean = false;

  selectExample(example: string) {
    const index = this.exampleQueries.indexOf(example);

    // Track example query click
    this.eventHandler.trackExampleQueryClicked(
      example,
      index,
      this.hasAISuggestions
    );

    // Clear previous report if selecting a new suggestion
    if (this.reportUrl) {
      this.reset();
    }
    this.query = example;
  }
  
  isAISuggestion(index: number): boolean {
    // AI suggestions start after first 2 default queries when present
    return this.hasAISuggestions && index >= 2;
  }

  async handleSubmit() {
    if (!this.query.trim()) return;

    // Clear any previous error and report data
    this.error = null;

    // Set loading state to show processing feedback to user
    this.loading = true;
    this.startProcessingAnimation();

    try {
      // Clear any saved session to prevent restoration of old agent
      localStorage.removeItem('assisty_active_session');

      // First, open the chatbox
      EventBus.$emit('chatbox:open');

      // Wait for chatbox to be open
      await this.waitForChatboxOpen();

      // Wait for chatbox to be fully ready (event-driven synchronization)
      try {
        await this.waitForChatboxReady();
      } catch (readyError: any) {
        // Log timeout/error but continue with fallback behavior
        console.warn('[ReportBuilder] Chatbox ready timeout:', readyError.message);
        // Add a small fallback delay to give chatbox a chance to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Execute the query using compound event for reliability
      const result = await this.executeReportBuilderQuery(this.query);

      if (!result.success) {
        throw new Error(result.error || 'Failed to process query');
      }

      // Don't clear the query here - let it be cleared when preview is shown
      // This keeps the UI consistent while processing

    } catch (error: any) {
      console.error('[ReportBuilder] Error:', error);
      this.error = error.message || 'Failed to connect to Report Builder agent. Please try again.';
      this.stopProcessingAnimation();
      this.loading = false;
    }

    // Loading will be turned off by handleAgentPreview when the report is generated
  }

  /**
   * Wait for chatbox to be opened
   */
  private waitForChatboxOpen(): Promise<void> {
    return new Promise((resolve) => {
      // Check if already open
      if (this.isChatboxOpen) {
        resolve();
        return;
      }

      // Wait for chatbox to open
      let attempts = 0;
      const checkInterval = setInterval(() => {
        if (this.isChatboxOpen || attempts > 20) { // Max 2 seconds wait
          clearInterval(checkInterval);
          resolve();
        }
        attempts++;
      }, 100);
    });
  }

  /**
   * Wait for chatbox to be fully ready (initialized and listeners registered)
   * Returns a Promise that resolves when 'chatbox:ready' event fires
   * Rejects if timeout (5 seconds) elapses before the event
   */
  private waitForChatboxReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      let timeoutId: number | null = null;

      // Set up one-time listener for chatbox ready event
      const readyHandler = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        resolve();
      };

      // Register one-time event listener
      EventBus.$once('chatbox:ready', readyHandler);
      EventBus.$emit('chatbox:check-status');
      // Set up 5-second timeout
      timeoutId = window.setTimeout(() => {
        // Remove the event listener if timeout occurs
        EventBus.$off('chatbox:ready', readyHandler);
        reject(new Error('Timeout waiting for chatbox to be ready'));
      }, 5000);
    });
  }

  /**
   * Execute a Report Builder query with proper sequencing and error handling
   * Uses compound event pattern for atomic operations
   */
  private executeReportBuilderQuery(query: string): Promise<any> {
    return new Promise((resolve) => {
      // Set up completion handler
      const completionHandler = (result: any) => {
        console.log('[ReportBuilder] Query complete:', result);
        EventBus.$off('report-builder:query-complete', completionHandler);
        resolve(result);
      };

      EventBus.$once('report-builder:query-complete', completionHandler);

      // Send compound command to chatbox (chatbox should already be open)
      console.log('[ReportBuilder] Sending report-builder-query event with:', query);
      EventBus.$emit('chatbox:report-builder-query', {
        query,
        timestamp: Date.now()
      });

      // Timeout after 15 seconds
      setTimeout(() => {
        console.log('[ReportBuilder] Query timeout - no response received');
        EventBus.$off('report-builder:query-complete', completionHandler);
        resolve({ success: false, error: 'Operation timeout. Please try again.' });
      }, 150000);
    });
  }

  startProcessingAnimation() {
    const stages: Array<'understanding' | 'analyzing' | 'generating'> = ['understanding', 'analyzing', 'generating'];
    let currentIndex = 0;
    
    this.processingStage = stages[0];
    
    this.stageInterval = setInterval(() => {
      currentIndex = (currentIndex + 1) % stages.length;
      this.processingStage = stages[currentIndex];
    }, 2000);
  }

  stopProcessingAnimation() {
    if (this.stageInterval) {
      clearInterval(this.stageInterval);
      this.stageInterval = null;
    }
    this.processingStage = 'understanding';
  }

  getConfidenceClass(score: number): string {
    if (score >= 0.8) return 'high-confidence';
    if (score >= 0.5) return 'medium-confidence';
    return 'low-confidence';
  }

  getExampleIcon(index: number): string {
    const icons = ['📊', '📈', '💰', '👥', '📦'];
    return icons[index % icons.length];
  }

  getProcessingMessage(): string {
    const messages = {
      'understanding': 'Parsing your natural language query...',
      'analyzing': 'Analyzing your data patterns and trends...',
      'generating': 'Preparing your customized report...'
    };
    return messages[this.processingStage] || 'Processing...';
  }

  showRefinementSuggestions() {
    // Track refinement request
    if (this.eventHandler && this.confidenceScore !== null) {
      this.eventHandler.trackRefinementRequested(this.confidenceScore);
    }
    
    // Placeholder for refinement suggestions
    console.log('Show refinement suggestions');
  }

  handlePreviewError(error: string) {
    console.error('Preview error:', error);
    // Optionally show a toast or notification to the user
  }

  /**
   * Handle agent preview event from AgentChatbox via EventBus
   *
   * This method is called when the Report Builder agent generates parameters
   * through the agent chat interface. It processes the parameters and displays
   * the report preview.
   *
   * @param reportData - Extracted parameters from the agent
   */
  handleAgentPreview(reportData: any) {
    console.log('[ReportBuilder] Received agent preview data:', reportData);

    // Turn off loading state when we receive the preview
    this.loading = false;
    
    this.stopProcessingAnimation();

    // Clear the query now that we've received a response
    this.query = '';

    // Check if this is a non-report response (greeting, help, unclear request)
    if (reportData && !reportData.success && reportData.error) {
      console.log('[ReportBuilder] Non-report response received:', reportData.error);

      // Clear any previous error since this is a valid response
      this.error = null;

      // Handle specific non-report response types
      if (reportData.error === 'greeting' ||
          reportData.error === 'non_report_query' ||
          reportData.error === 'missing_information') {
        // Don't show error - the agent is handling the conversation appropriately
        console.log('[ReportBuilder] Agent is handling conversation:', reportData.explain_message);

        // Track agent response type for non-report responses
        const responseType = reportData.error === 'greeting' ? 'greeting' :
                            reportData.error === 'non_report_query' ? 'help' :
                            'clarification';
        this.eventHandler.trackAgentResponseType({
          responseType: responseType as 'greeting' | 'help' | 'clarification',
          requiresUserAction: responseType === 'clarification',
          processingTimeMs: reportData.processing_time_ms || 0,
          messageNumber: reportData.message_number || 1
        });

        // Emit completion event to remove any loading message
        EventBus.$emit('report-builder:preview-complete');
        return;
      }
    }

    // Validate that we have successful parameters for report generation
    if (!reportData || !reportData.success || !reportData.parameters) {
      console.warn('[ReportBuilder] Invalid report data received:', reportData);

      // Track agent response type for unclear/failed responses
      this.eventHandler.trackAgentResponseType({
        responseType: 'unclear',
        requiresUserAction: true,
        processingTimeMs: reportData?.processing_time_ms || 0,
        messageNumber: reportData?.message_number || 1
      });

      // Only show error if it's not a recognized non-report response
      if (!reportData || !reportData.error) {
        this.error = 'Invalid report parameters received from agent';
      }

      // Emit completion event to remove any loading message
      EventBus.$emit('report-builder:preview-complete');
      return;
    }

    const { parameters, report_name, confidence_score, suggested_questions } = reportData;

    // Set the report name
    this.reportName = report_name || 'AI Generated Report';

    // Store the full AI response for the preview component
    this.aiResponse = reportData;

    // Update smart suggestions if available
    if (suggested_questions && suggested_questions.length > 0) {
      this.exampleQueries = suggested_questions;
      this.hasAISuggestions = true;

      // Track AI suggestions generated
      this.eventHandler.trackAISuggestionGenerated(suggested_questions);
    }

    // Generate the report query for navigation
    const reportQuery = generateReportQuery({
      ...parameters,
      report_name: report_name || 'AI Generated Report',
      analyticsType: 'Top High'
    });

    // Construct the report URL - handle special measure mappings
    let measure = parameters.measure;
    if (measure === 'actual_inventory_quantity') {
      measure = 'inventory-quantity';
    }

    // Convert report name into format report_name_with_underscore_and_lowercase
    let reportNameUpdated = report_name
      ? report_name.replace(/ /g, '-').toLowerCase()
      : 'custom-report';

    this.reportUrl = `/analytics/${measure}/${reportNameUpdated}?report_query=${reportQuery}`;
    this.confidenceScore = confidence_score || 0;

    // Keep the success section collapsed by default to save space
    this.successSectionCollapsed = true;

    // Track successful report generation via agent
    this.eventHandler.trackReportGenerated(true, this.reportUrl);

    // Track agent response type for successful report generation
    this.eventHandler.trackAgentResponseType({
      responseType: 'report_generation',
      confidenceScore: confidence_score,
      requiresUserAction: false,
      processingTimeMs: reportData.processing_time_ms || 0,
      messageNumber: reportData.message_number || 1
    });

    console.log('[ReportBuilder] Preview displayed successfully', {
      measure,
      reportUrl: this.reportUrl,
      confidence: this.confidenceScore
    });

    // Emit completion event to remove loading message in AgentChatbox
    console.log('[ReportBuilder] Emitting preview-complete event');
    EventBus.$emit('report-builder:preview-complete');
  }

  reset() {
    this.query = '';
    this.error = null;
    this.reportUrl = null;
    this.reportName = null;
    this.confidenceScore = null;
    this.aiResponse = null;
    // Reset suggestions to default
    this.exampleQueries = [...this.defaultQueries];
    this.hasAISuggestions = false;
    // Reset collapse state
    this.successSectionCollapsed = true;
  }

  /**
   * Opens the demo booking page in a new tab
   */
  openDemoBooking() {
    this.$router.push('/live-demo');
  }

  mounted() {
    // Initialize event tracking
    this.eventHandler = new ReportBuilderEventHandler();

    // Automatically enable onboarding mode if data is not ready
    // This shows sample data while the import is in progress
    if (!this.isAthenaReady) {
      this.onboardingMode = true;
      try {
        localStorage.setItem('assisty_onboarding_mode', 'true');
        console.log('[ReportBuilder] Onboarding mode activated - data not ready');
      } catch (error) {
        console.warn('[ReportBuilder] Failed to save onboarding mode to localStorage:', error);
      }
    } else {
      // Data is ready, ensure onboarding mode is disabled
      this.onboardingMode = false;
      try {
        localStorage.removeItem('assisty_onboarding_mode');
        console.log('[ReportBuilder] Data ready - onboarding mode disabled');
      } catch (error) {
        console.warn('[ReportBuilder] Failed to remove onboarding mode from localStorage:', error);
      }
    }

    // Track page view with referrer if available
    const referrer = this.$route.query.ref as string || this.$route.query.source as string;
    this.eventHandler.trackReportBuilderViewed(referrer);

    // Register EventBus listener for agent preview events
    EventBus.$on('report-builder:preview', this.handleAgentPreview);

    // Register EventBus listeners for chatbox state changes
    EventBus.$on('chatbox:opened', this.onChatboxOpened);
    EventBus.$on('chatbox:closed', this.onChatboxClosed);

    // Register EventBus listeners for agent-specific events
    // Store handler references for proper cleanup
    this.onAgentSelectedHandler = (data: any) => {
      this.eventHandler.trackAgentSelected(data);
    };
    EventBus.$on('report-builder:agent-selected', this.onAgentSelectedHandler);

    this.onConversationQualityHandler = (data: any) => {
      this.eventHandler.trackAgentConversationQuality(data);
    };
    EventBus.$on('report-builder:conversation-quality', this.onConversationQualityHandler);

    // Check if chatbox is already open when navigating to this page
    // If it is, auto-select Report Builder agent
    this.$nextTick(() => {
      // Check if chatbox is open by emitting a status check event
      // The chatbox will respond if it's open
      EventBus.$emit('chatbox:check-status');

      // Small delay to allow chatbox to respond
      setTimeout(() => {
        // Check if chatbox responded that it's open
        let chatboxStatus: string | null = null;
        try {
          chatboxStatus = localStorage.getItem('assisty_chatbox_open');
        } catch (error) {
          console.warn('[ReportBuilder] Failed to read chatbox status from localStorage:', error);
        }

        if (chatboxStatus === 'true') {
          this.isChatboxOpen = true;

          // Check current selected agent to see if we need to switch
          let currentAgent: string | null = null;
          try {
            currentAgent = localStorage.getItem('assisty_last_selected_agent');
          } catch (error) {
            console.warn('[ReportBuilder] Failed to read selected agent from localStorage:', error);
          }

          let needsSelection = true;

          if (currentAgent) {
            try {
              const agent = JSON.parse(currentAgent);
              needsSelection = agent.id !== 'report-builder';
            } catch (e) {
              // Parse error, proceed with selection
            }
          }

          if (needsSelection) {
            console.log('[ReportBuilder] Chatbox already open on page mount, auto-selecting Report Builder agent');
            EventBus.$emit('chatbox:select-agent', 'report-builder');
          }

          // Track agent conversation started when chatbox is already open on page navigation
          this.eventHandler.trackAgentConversationStarted(
            'report-builder',
            this.eventHandler.getSessionId(), // Use public getter for encapsulation
            'page_navigation'
          );
        }
      }, 200);
    });

    // Focus on the input when component mounts (only if chatbox is not open)
    this.$nextTick(() => {
      setTimeout(() => {
        if (!this.isChatboxOpen) {
          const textarea = document.querySelector('.query-input') as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
          }
        }
      }, 100);
    });
  }

  onChatboxOpened() {
    this.isChatboxOpen = true;

    // Track chatbox opened event
    const source = this.loading && this.query.trim() ? 'query_submission' : 'button';
    this.eventHandler.trackChatboxOpened(source, 'report-builder');

    // When on Report Builder page and chatbox opens manually (not from submit),
    // auto-select Report Builder agent for better UX
    if (!this.loading && !this.query.trim()) {
      // Small delay to ensure chatbox is initialized
      setTimeout(() => {
        // Check current selected agent to avoid unnecessary re-selection
        let currentAgent: string | null = null;
        try {
          currentAgent = localStorage.getItem('assisty_last_selected_agent');
        } catch (error) {
          console.warn('[ReportBuilder] Failed to read selected agent from localStorage:', error);
        }

        let needsSelection = true;

        if (currentAgent) {
          try {
            const agent = JSON.parse(currentAgent);
            needsSelection = agent.id !== 'report-builder';
          } catch (e) {
            // Parse error, proceed with selection
          }
        }

        if (needsSelection) {
          console.log('[ReportBuilder] Auto-selecting Report Builder agent for manual chatbox open');
          EventBus.$emit('chatbox:select-agent', 'report-builder');
        }
      }, 300);
    }
  }

  onChatboxClosed(data?: { interactionCount?: number }) {
    this.isChatboxOpen = false;

    // Track chatbox closed event with real interaction count from chatbox
    const interactionCount = data?.interactionCount || 0;
    const reportGenerated = !!this.reportUrl;
    this.eventHandler.trackChatboxClosed(interactionCount, reportGenerated);
  }

  formatMeasure(measure: string): string {
    const measureLabels: Record<string, string> = {
      'sales_overview': 'Sales Overview',
      'actual_inventory_quantity': 'Current Inventory',
      'inventory_forecasting': 'Inventory Forecast',
      'inventory_replenishment': 'Reorder Recommendations',
      'lost_revenue': 'Lost Revenue Analysis',
      'stock_availability': 'Stock Availability',
      'inventory_burndown': 'Inventory Burndown'
    };
    return measureLabels[measure] || measure.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  formatTimeRange(timeRange: any): string {
    if (!timeRange) return 'All time';
    
    if (timeRange.period) {
      const periodLabels: Record<string, string> = {
        'last-week': 'Last Week',
        'last-month': 'Last Month',
        'last-quarter': 'Last Quarter',
        'last-year': 'Last Year',
        'this-fiscal-year': 'This Fiscal Year',
        '7d': 'Last 7 Days',
        '30d': 'Last 30 Days',
        '90d': 'Last 90 Days',
        'same-month-last-year': 'Same Month Last Year',
        'same-week-last-year': 'Same Week Last Year',
        'same-quarter-last-year': 'Same Quarter Last Year'
      };
      return periodLabels[timeRange.period] || timeRange.period;
    }
    
    if (timeRange.from && timeRange.to) {
      const from = new Date(timeRange.from).toLocaleDateString();
      const to = new Date(timeRange.to).toLocaleDateString();
      return `${from} - ${to}`;
    }
    
    return 'Custom period';
  }
  
  formatDimension(dimension: string): string {
    const dimensionLabels: Record<string, string> = {
      'by_product': 'Product',
      'by_variant': 'Product Variant',
      'by_day': 'Day',
      'by_week': 'Week',
      'by_month': 'Month',
      'by_quarter': 'Quarter',
      'by_year': 'Year',
      'by_customer': 'Customer',
      'by_order': 'Order',
      'by_category': 'Category',
      'by_vendor': 'Vendor',
      'by_location': 'Location'
    };
    return dimensionLabels[dimension] || dimension.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  formatSort(sort: any): string {
    if (!sort) return 'Default order';
    const direction = sort.direction === 'desc' ? '↓' : '↑';
    const column = sort.column?.replace(/[\[\]]/g, '').replace(/_/g, ' ');
    return `${column} ${direction}`;
  }
  
  formatFilter(filter: any): string {
    const operatorLabels: Record<string, string> = {
      'eq': '=',
      'ne': '≠',
      'gt': '>',
      'gte': '≥',
      'lt': '<',
      'lte': '≤',
      'contains': 'contains',
      'notContains': 'does not contain',
      'in': 'in',
      'null': 'is empty',
      'notNull': 'is not empty'
    };
    
    // Format the filter field name - remove underscores and capitalize words
    let fieldName = (filter.name || filter.code || 'field')
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/\b\w/g, (l: string) => l.toUpperCase());  // Capitalize first letter of each word
    
    const operator = operatorLabels[filter.operator] || filter.operator;
    
    // Format the value
    let formattedValue = '';
    if (filter.operator === 'null') {
      return `${fieldName} is empty`;
    } else if (filter.operator === 'notNull') {
      return `${fieldName} is not empty`;
    } else if (Array.isArray(filter.value)) {
      formattedValue = filter.value.map((v: any) => `"${v}"`).join(', ');
    } else {
      formattedValue = `"${filter.value}"`;
    }
    
    return `${fieldName} ${operator} ${formattedValue}`;
  }

  async saveAsCustomReport() {
    if (!this.aiResponse || !this.aiResponse.parameters || !this.reportName) {
      EventBus.$emit('show-snackbar', { 
        message: 'Cannot save report: Missing report data', 
        color: 'error' 
      });
      return;
    }

    const saveStartTime = Date.now();
    
    // Track save button clicked
    this.eventHandler.trackSaveReportClicked();

    this.loadingSaveReport = true;
    
    try {
      // Create a new custom report instance
      const customReport = new CustomReportModel();
      
      // Set basic properties
      customReport.name = this.reportName;
      
      // Set measure
      customReport.measure = new MeasureModel();
      customReport.measure.code = this.aiResponse.parameters.measure;
      
      // Set dimension if present
      if (this.aiResponse.parameters.dimension) {
        customReport.dimension = new DimensionModel();
        customReport.dimension.code = this.aiResponse.parameters.dimension;
      }
      
      // Set time range
      if (this.aiResponse.parameters.time_range) {
        customReport.filterTimerange = [
          this.aiResponse.parameters.time_range.from,
          this.aiResponse.parameters.time_range.to
        ];
      }
      
      // Set columns
      if (this.aiResponse.parameters.columns && this.aiResponse.parameters.columns.length > 0) {
        customReport.reportColumns = this.aiResponse.parameters.columns;
      }
      
      // Set filters if present
      if (this.aiResponse.parameters.filters && this.aiResponse.parameters.filters.length > 0) {
        // Convert filters to the format expected by CustomReportModel
        customReport.filterColumns.items = this.aiResponse.parameters.filters.map((filter: any) => ({
          code: filter.code || filter.column,
          name: filter.name || filter.column,
          operator: filter.operator,
          value: Array.isArray(filter.value) ? filter.value : [filter.value],
          dataType: filter.dataType || 'string'
        }));
      }
      
      // Generate and set the report query
      const reportQuery = generateReportQuery({
        ...this.aiResponse.parameters,
        report_name: this.reportName,
        analyticsType: 'Top High'
      });
      customReport.reportQuery = reportQuery;
      
      // Save the custom report
      await customReport.save();
      
      // Track successful save
      this.eventHandler.trackReportSaved({
        customReportId: String(customReport.id),
        reportName: customReport.name,
        timeToSaveSeconds: (Date.now() - saveStartTime) / 1000,
        modificationsMode: false
      });
      
      // Show different message and URL if data is not ready
      if (!this.isAthenaReady) {
        this.savedReportMessage = 'Report configuration saved! Data will be available once import is complete.';
        this.savedReportUrl = '/my-reports';
        this.savedReportLinkText = 'View all reports';
      } else {
        this.savedReportMessage = 'Report saved successfully!';
        this.savedReportUrl = `/my-reports/${customReport.id}`;
        this.savedReportLinkText = 'View saved report';
      }
      
      // Don't show EventBus notification - we have the custom notification with link
      
      this.loadingSaveReport = false;
      
    } catch (error: any) {
      console.error('Error saving custom report:', error);
      this.loadingSaveReport = false;
      
      // Track save failure
      this.eventHandler.trackSaveReportFailed(error.message || 'Unknown error');
      
      EventBus.$emit('show-snackbar', { 
        message: `Failed to save report: ${error.message || 'Unknown error'}`, 
        color: 'error' 
      });
    }
  }

  async editReport() {
    // Generate and set the report query
    const reportQuery = generateReportQuery({
        ...this.aiResponse.parameters,
        report_name: this.reportName,
        analyticsType: 'Top High'
      });

    // generate report url
    const reportNameFormatted = this.reportName ? this.reportName.replace(/ /g, '-').toLowerCase() : 'custom-report';
    const reportUrl = `/analytics/${this.aiResponse.parameters.measure}/${reportNameFormatted}?report_query=${reportQuery}`;
    this.$router.push(reportUrl);
  }

  beforeDestroy() {
    this.stopProcessingAnimation();

    // Clear data ready timeout to prevent callback after unmount
    if (this.dataReadyTimeout) {
      clearTimeout(this.dataReadyTimeout);
      this.dataReadyTimeout = null;
    }

    // Clean up EventBus listeners using stored handler references
    EventBus.$off('report-builder:preview', this.handleAgentPreview);
    EventBus.$off('chatbox:opened', this.onChatboxOpened);
    EventBus.$off('chatbox:closed', this.onChatboxClosed);

    // Remove agent-specific handlers using stored references
    if (this.onAgentSelectedHandler) {
      EventBus.$off('report-builder:agent-selected', this.onAgentSelectedHandler);
    }
    if (this.onConversationQualityHandler) {
      EventBus.$off('report-builder:conversation-quality', this.onConversationQualityHandler);
    }

    // Track session abandonment if user leaves without saving
    if (this.eventHandler && !this.savedReportUrl) {
      let stage: 'view' | 'query' | 'report_generated' = 'view';

      if (this.reportUrl) {
        stage = 'report_generated';
      } else if (this.query && this.query.trim()) {
        stage = 'query';
      }

      this.eventHandler.trackSessionAbandoned(stage);
    }
  }
}
